'use client';
import dynamic from 'next/dynamic';
import { useState, useEffect } from 'react';

import { cn } from '@app/libs/utils';

// Dynamic import MapView để tránh lỗi SSR
const MapView = dynamic(() => import('../ui/MapView'), {
  ssr: false,
  loading: () => (
    <div
      style={{
        height: '100%',
        width: '100%',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f3f4f6',
      }}
    >
      <div className="text-gray-500">Đang tải bản đồ...</div>
    </div>
  ),
});

interface MapSearchProps {
  className?: string;
  onSearch: (params: any) => void;
  lat?: number;
  lng?: number;
  onLocationChange?: (...args: any[]) => void;
  radius?: number;
  markers?: { lat: number; lng: number; toolip: React.ReactNode; popup: React.ReactNode }[];
}

export const MapSearch: React.FC<MapSearchProps> = ({
  className,
  lat,
  lng,
  onLocationChange,
  radius: propRadius,
  markers,
}) => {
  const [radius, setRadius] = useState<number>(propRadius ?? 1000);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    document
      .querySelector('body')
      ?.classList.add('fixed', 'h-screen', 'w-screen', 'overflow-hidden');
    return () => {
      document
        .querySelector('body')
        ?.classList.remove('fixed', 'h-screen', 'w-screen', 'overflow-hidden');
    };
  }, []);

  useEffect(() => {
    onLocationChange?.(lat, lng, radius);
  }, [lat, lng, propRadius]);

  const handleRadiusChange = (value: number) => {
    setRadius(value);
    onLocationChange?.(lat, lng, value);
  };

  const formatRadius = (meters: number): string => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(1)} km`;
    }
    return `${meters} m`;
  };

  if (!isClient) {
    return (
      <div
        className={cn('w-full relative z-0', className)}
        style={{
          height: '100%',
          width: '100%',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f3f4f6',
        }}
      >
        <div className="text-gray-500">Đang tải bản đồ...</div>
      </div>
    );
  }

  return (
    <div className={cn('w-full relative z-0', className)}>
      <MapView
        lat={lat}
        lng={lng}
        height="100%"
        width="100%"
        onLocationChange={(lat, lng) => onLocationChange?.(lat, lng, radius ?? 1000)}
        radius={radius}
        markers={markers}
        className="z-0"
      />
      <div
        className={cn(
          'absolute bottom-10 left-0 md:left-1/2 transform md:-translate-x-1/2 bg-white p-4 rounded-lg box-shadow-md z-101 w-80',
          'max-w-[calc(100vw-150px)] ml-5'
        )}
      >
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <label className="text-sm font-medium text-gray-700">Bán kính tìm kiếm</label>
            <span
              style={{
                fontSize: '14px',
                fontWeight: 600,
                color: '#007bff',
                minWidth: '60px',
                textAlign: 'right',
              }}
            >
              {formatRadius(radius)}
            </span>
          </div>
          <input
            type="range"
            min={1000}
            max={20000}
            step={100}
            value={radius}
            onChange={e => handleRadiusChange(Number(e.target.value))}
            style={{
              width: '100%',
              height: '6px',
              borderRadius: '3px',
              background: `linear-gradient(to right, #007bff ${((radius - 1000) / (20000 - 1000)) * 100}%, #e5e7eb ${((radius - 1000) / (20000 - 1000)) * 100}%)`,
              outline: 'none',
              cursor: 'pointer',
              WebkitAppearance: 'none',
            }}
          />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '12px',
              color: '#6b7280',
            }}
          >
            <span>1 km</span>
            <span>20 km</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapSearch;
